import { useEffect, useRef } from 'react';

export interface PollParameter {
  cancelPolling: () => void;
}

interface BasePollingParameter {
  interval: number;
  onPoll: (parameter: PollParameter) => void;
  maxDuration: number;
  maxAttempts?: number;
  onTimedOut?: () => void;
}

interface WithOnCancel extends BasePollingParameter {
  onCancel: () => void;
  shouldInvokeOnCancelOnUnmount: boolean;
}

interface WithoutOnCancel extends BasePollingParameter {
  onCancel?: never;
  shouldInvokeOnCancelOnUnmount?: never;
}

type UsePollingParameter = WithOnCancel | WithoutOnCancel;

interface UsePollingReturn {
  cancelPolling: (invokeOnCancel?: boolean) => void;
  startPolling: () => void;
}
export default function usePolling({
  onPoll,
  onCancel,
  onTimedOut,
  interval = 5000,
  maxDuration,
  maxAttempts,
  shouldInvokeOnCancelOnUnmount,
}: UsePollingParameter): UsePollingReturn {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const maxTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attemptRef = useRef<number | null>(0);

  function cancelPolling(invokeOnCancel = true) {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
    }
    if (invokeOnCancel && onCancel) {
      onCancel();
    }
    attemptRef.current = 0;
  }

  function handleInterval() {
    if (maxAttempts !== undefined && attemptRef.current >= maxAttempts) {
      cancelPolling();
      if (onTimedOut) {
        onTimedOut();
      }
      return;
    }

    attemptRef.current += 1;
    onPoll({
      cancelPolling,
    });
  }

  function startPolling() {
    attemptRef.current = 0;
    intervalRef.current = setInterval(handleInterval, interval);

    maxTimeoutRef.current = setTimeout(() => {
      cancelPolling();
      if (onTimedOut) {
        onTimedOut();
      }
    }, maxDuration);
  }

  useEffect(() => {
    return () => cancelPolling(shouldInvokeOnCancelOnUnmount);
  }, []);

  return {
    cancelPolling,
    startPolling,
  };
}
